import {
  Component,
  OnDestroy,
  inject,
  Output,
  EventEmitter,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { CommonModule } from '@angular/common';
import { NzInputModule } from 'ng-zorro-antd/input';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { DrawerService } from '../../../../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../../../../core/constants/drawer-ids.constant';
import { FrequencyDrawerParams } from '../../../insights-data.interface';
import { ErrorResponse } from '../../../../../../../core/models/error.models';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { LoaderService } from '../../../../../../../core/services/loader.service';
import { AuditContextService } from '../../../../../../audit-logs/audit-context.service';
import { AuditFieldChange } from '../../../../../../audit-logs/audit-logs.interface';
import { InsightsDataService } from '../../../insights-data.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { AllowOnlyValidNumbersDirective } from '../../../../../../../core/direactives/allowedOnlyValidNUmber.directive';
import _ from 'lodash';
import { EnterDataSaveNextUtility } from '../../enter-data-save-next.utility';
import { SetDurationComponent } from '../../set-duration/set-duration.component';
@Component({
  selector: 'app-frequency-only',
  standalone: true,
  imports: [
    NzFormModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzInputModule,
    CommonModule,
    NzButtonModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    AllowOnlyValidNumbersDirective,
    NzSpinModule,
  ],
  providers: [provideNgxMask()],
  templateUrl: './frequency-only.component.html',
  styleUrl: './frequency-only.component.scss',
})
export class FrequencyOnlyComponent implements OnDestroy {
  private readonly auditContextService = inject(AuditContextService);
  private readonly destroy$ = new Subject<void>();
  drawerParmas: FrequencyDrawerParams = {} as FrequencyDrawerParams;
  frquencyIntervalForm!: FormGroup;
  loading$: Observable<boolean> = of(false);
  @Output() saveAndNext = new EventEmitter<string>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly drawerService: DrawerService,
    private readonly commonService: CommonService,
    private readonly loaderService: LoaderService,
    private readonly insightsService: InsightsDataService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.frquencyIntervalForm = this.fb.group({
      id: [0],
      frequency: [''],
    });
    this.setupDrawerSubscription();
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<FrequencyDrawerParams>(DRAWER_IDS.FREQUENCY)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParmas = params;
          this.frquencyIntervalForm.reset({ id: 0, frequency: '' });
          const matchedData = _.find(
            this.drawerParmas.behaviorData.activityDataUnitValues,
            { dataDate: this.drawerParmas.selectedDate }
          );
          if (matchedData) {
            this.frquencyIntervalForm.patchValue({
              id: matchedData.id,
              frequency: matchedData.blockValue,
            });
          } else if (this.drawerParmas.selectedRow.totalFrequency) {
            this.frquencyIntervalForm.patchValue({
              frequency: this.drawerParmas.selectedRow.totalFrequency,
            });
          }
        }
      });
  }

  get hasNextDate(): boolean {
    return EnterDataSaveNextUtility.shouldShowSaveAndNext(
      this.drawerParmas.selectedUnit,
      this.drawerParmas.selectedDate
    );
  }

  get hasNextDateForUnit1(): boolean {
    return (
      this.drawerParmas.selectedUnit === 1 &&
      EnterDataSaveNextUtility.getNextWorkingDate(
        this.drawerParmas.selectedDate
      ) !== null
    );
  }

  get nextDateHasDuration(): boolean {
    return EnterDataSaveNextUtility.getNextDateInfo(
      this.drawerParmas.behaviorData,
      this.drawerParmas.selectedDate
    ).hasDuration;
  }

  get nextDateHasFrequency(): boolean {
    return EnterDataSaveNextUtility.getNextDateInfo(
      this.drawerParmas.behaviorData,
      this.drawerParmas.selectedDate
    ).hasFrequency;
  }

  get nextDateHasNoData(): boolean {
    return EnterDataSaveNextUtility.getNextDateInfo(
      this.drawerParmas.behaviorData,
      this.drawerParmas.selectedDate
    ).hasNoData;
  }

  continueWithFrequencyOnly(): void {
    this.saveFrequencyValue(() => {
      const nextDate = EnterDataSaveNextUtility.getNextWorkingDate(
        this.drawerParmas.selectedDate
      );
      if (nextDate) {
        this.saveAndNext.emit(nextDate);
      }
    });
  }
  continueWithDuration(): void {
    this.saveFrequencyValue(() => {
      this.commonService.notify('success', 'Data saved successfully');
      const nextDate = EnterDataSaveNextUtility.getNextWorkingDate(
        this.drawerParmas.selectedDate
      );
      if (nextDate) {
        this.openDurationDrawer(nextDate);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  submitForm() {
    this.saveFrequencyValue(() => {
      this.commonService.notify('success', `Data saved successfully`);
      this.drawerParmas.onDataSaved?.();
      this.onCancel();
    });
  }

  private saveFrequencyValue(onSuccess: () => void): void {
    const payload = this.buildFrequencyPayload();
    this.loaderService.show();
    this.insightsService
      .saveBlockValue(payload)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: onSuccess,
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  private buildFrequencyPayload(): {
    unitId: number;
    blockValues: { id: number; dataDate: string; unitValue: number }[];
    _modifiedFields: AuditFieldChange[];
  } {
    const original = {
      id: this.frquencyIntervalForm.value.id,
      frequency: this.drawerParmas.selectedRow.totalFrequency || '',
    };
    const updated = {
      id: this.frquencyIntervalForm.value.id,
      frequency: this.frquencyIntervalForm.value.frequency,
    };
    const modifiedFields = this.auditContextService.buildFieldChanges(
      original,
      updated,
      {
        sensitiveFields: [],
        labelMap: {
          frequency: `Total Frequency | ${this.drawerParmas.selectedUnitName ?? ''} (${this.drawerParmas.selectedDate})`,
        },
        excludeFields: ['id'],
      }
    );

    return {
      unitId: this.drawerParmas.unitId,
      blockValues: [
        {
          id: this.frquencyIntervalForm.value.id,
          dataDate: this.drawerParmas.selectedDate,
          unitValue: Number(this.frquencyIntervalForm.value.frequency),
        },
      ],
      _modifiedFields: modifiedFields,
    };
  }

  private openDurationDrawer(nextDate: string): void {
    const updatedParams: FrequencyDrawerParams = {
      ...this.drawerParmas,
      selectedDate: nextDate,
      selectedDay: EnterDataSaveNextUtility.getDayName(nextDate),
      selectedRow: {
        id: 0,
        date: nextDate,
        day: EnterDataSaveNextUtility.getDayName(nextDate),
        individualIntervalDetails: '',
        totalFrequency: '',
        totalDuration: '',
        averageDuration: '',
        action: '',
        isFrequencyValue: false,
        isDuration: false,
        isUpdateTotal: false,
        isFrequencyOnly: false,
        commentCount: 0,
      },
    };

    this.drawerService.closeDrawer(DRAWER_IDS.FREQUENCY);
    this.drawerService.openDrawer(
      SetDurationComponent,
      this.drawerParmas.durationDrawerTitleTemplate ?? 'Data',
      1170,
      true,
      undefined,
      updatedParams,
      DRAWER_IDS.DURATION
    );
  }

  onCancel() {
    this.drawerService.closeDrawer(DRAWER_IDS.FREQUENCY);
  }
  submitAndNext(): void {
    this.saveFrequencyValue(() => {
      this.commonService.notify('success', 'Data saved successfully');
      const nextDate = EnterDataSaveNextUtility.getNextWorkingDate(
        this.drawerParmas.selectedDate
      );
      if (nextDate) {
        this.saveAndNext.emit(nextDate);
      }
    });
  }
}
